// Превръща суровия одит запис (заявка + отговор, вече в базата) в структуриран
// "разказ" на човешки език. Чисто презентационен слой — не пипа база/бекенд.
//
// Класификацията е по (метод, път), който е стабилен (за разлика от
// локализирания етикет `operation`). Заглавието на картата идва директно от
// `operation`, така че винаги съвпада с колоната в таблицата.
//
// ВСИЧКИ имена на полета, пътища и магически стойности идват от `contract.ts`
// (едно място за промяна при смяна на API контракта).

import type { TFunction } from "i18next";
import type { AuditLogDetail } from "../../../../api/auditLogs";
import type { AuditLookups } from "./useAuditLookups";
import i18n from "../../../../translations/i18n";
import { eur, qty, pct, pickItemName, toNumber } from "./format";
import {
  PATHS,
  BILL_ACTION_KIND,
  BILL_ACTION_KIND_2,
  MODULE_PATHS,
  INVENTORY_DOC_KIND,
  REQ,
  RES,
  FISCAL,
  NEW_BILL_ID,
} from "./contract";
import {
  asArr,
  asObj,
  asString,
  emptyStory,
  field,
  isTruncated,
} from "./story";
import { interpretInventory } from "./interpretInventory";
import { interpretSystem } from "./interpretSystem";
import {
  classifyReservationAction,
  interpretReservation,
  reservationKindTone,
} from "./interpretReservations";

// Типовете на разказа живеят в `story.ts` (за да ги ползват и модулните
// интерпретатори без кръгов импорт), но се преизнасят оттук — това е входната
// точка на слоя и целият останал код ги внася от нея.
export type { Tone, Fact, AddonLine, Line, HumanStory } from "./story";
import type { Tone, HumanStory, Line, AddonLine } from "./story";

// ── Класификация ────────────────────────────────────────────────────────────

export function classifyAction(
  method: string,
  path: string,
  body?: unknown,
): string {
  const m = method.toUpperCase();
  if (m === "POST" && path === PATHS.billsCollection) {
    const billId = toNumber(field(body, REQ.billId));
    // Без тяло (напр. в хронологията) не различаваме нова от добавяне —
    // тонът/иконата им съвпадат, а етикетът идва от записа.
    return billId === NEW_BILL_ID ? "new" : "add";
  }
  const bill = path.match(PATHS.billSubpath);
  if (bill) return BILL_ACTION_KIND[bill[1]] ?? "generic";
  const bill2 = path.match(PATHS.billSubpath2);
  if (bill2) return BILL_ACTION_KIND_2[`${bill2[1]}/${bill2[2]}`] ?? "generic";
  // Същите факти като смяната на един сервитьор (waiterId в тялото).
  if (m === "PATCH" && path === PATHS.transferAllWaiter) return "changeWaiter";
  if (m === "POST" && path === PATHS.cashIn) return "cashIn";
  if (m === "POST" && path === PATHS.cashOut) return "cashOut";
  return classifyModuleAction(m, path);
}

/** Класификация на модулите извън „Маркиране" (склад, конфигурация, печат…). */
function classifyModuleAction(method: string, path: string): string {
  const inventoryDoc = path.match(MODULE_PATHS.inventoryDoc);
  if (inventoryDoc) return INVENTORY_DOC_KIND[inventoryDoc[1]] ?? "generic";
  if (MODULE_PATHS.inventoryRestore.test(path)) return "invRestore";
  if (method === "POST" && MODULE_PATHS.inventoryDeliveryNotes.test(path))
    return "invDeliveryNotes";
  if (method === "PUT" && path === MODULE_PATHS.inventorySettings)
    return "invSettings";
  if (MODULE_PATHS.inventoryProductWarehouse.test(path)) return "invProductWarehouse";
  // Само PUT: GET-овете на конфигурацията са извън одита, но хронологията може
  // да поднесе стар запис — тогава „новата стойност" би излъгала.
  if (method === "PUT" && MODULE_PATHS.configuration.test(path))
    return "configSetting";
  if (MODULE_PATHS.invoicePrint.test(path)) return "invoicePrint";
  if (MODULE_PATHS.establishmentPrint.test(path)) return "establishmentPrint";
  if (path === MODULE_PATHS.voiceParseOrder) return "voiceOrder";
  if (MODULE_PATHS.deviceFiscalUpdate.test(path)) return "deviceUpdate";
  return classifyReservationAction(method, path) ?? "generic";
}

/** Тонът (цвят) на категория действие — ползва се и от хронологията. */
export function kindTone(kind: string): Tone {
  switch (kind) {
    case "new":
    case "add":
      return "add";
    case "applyDiscount":
    case "applyItemDiscount":
    case "applyItemDiscounts":
      return "discount";
    case "removeDiscount":
    case "removeItemDiscount":
    case "cancelItem":
    case "cancelItems":
    case "cancelAll":
    case "invWriteoff":
    case "invReportPosting":
      return "remove";
    case "close":
    case "cashIn":
    case "cashOut":
    case "splitAndPay":
    case "fiscalRetry":
    case "stornoRetry":
      return "money";
    case "restore":
    case "split":
    case "rename":
    case "changeWaiter":
    case "setItemPrices":
    case "invStocktake":
    case "invTransfer":
    case "invSettings":
    case "invProductWarehouse":
    case "invRestore":
    case "invDeliveryNotes":
    case "configSetting":
    case "preliminaryPrint":
    case "invoicePrint":
    case "establishmentPrint":
    case "deviceUpdate":
    case "voiceOrder":
      return "info";
    case "invDelivery":
      return "add";
    default:
      return reservationKindTone(kind) ?? "neutral";
  }
}

/** Причината в `detail` на отказа: низ или `{bg, en}` — на езика на интерфейса. */
function detailText(raw: unknown): string | undefined {
  const text = asString(raw);
  if (text) return text;
  const localized = asObj(raw);
  if (!localized) return undefined;
  const lang = i18n.language?.split("-")[0] ?? "bg";
  return asString(localized[lang]) ?? asString(localized.bg) ?? undefined;
}

/**
 * Семантичен резултат на операцията. Освен HTTP кода хваща и случая, в който
 * заявката връща 200, но фискализацията е пропаднала (`fiscal_result.status`),
 * което за управителя е „неуспешно", не „успешно".
 */
export function statusInfo(detail: AuditLogDetail): {
  ok: boolean;
  reason?: string;
} {
  const code = detail.status_code;
  const httpOk = code >= 200 && code < 300;
  const resp = asObj(detail.response_body);
  const fr = asObj(resp?.[RES.fiscalResult]);
  const fiscalFailed = !!fr && fr[FISCAL.statusField] === FISCAL.failedValue;
  let reason: string | undefined;
  if (fr) reason = asString(fr[FISCAL.errorField]) ?? undefined;
  if (!reason && !httpOk && resp) reason = detailText(resp[RES.detail]);
  return {
    ok: httpOk && !fiscalFailed,
    reason: !httpOk || fiscalFailed ? reason : undefined,
  };
}

/** Салдото на сметката след операцията (от вече записания отговор). */
export function billBalanceAfter(detail: AuditLogDetail): number | null {
  const direct = toNumber(field(detail.response_body, RES.totalAmount));
  if (direct !== null) return direct;
  // split / splitAndPay връщат { oldBill, newBill } — салдото на
  // проследяваната (старата) сметка е след преместването навън.
  const oldBill = field(detail.response_body, RES.splitOldBill);
  return toNumber(field(oldBill, RES.totalAmount));
}

// ── Резолване на име на продукт по bill_element_item id (от отговора) ────────

interface ResolvedItem {
  name: string;
  unit: number | null;
  quantity: number | null;
}

function resolveBillItem(
  response: unknown,
  elementItemId: number | null,
  lookups: AuditLookups,
): ResolvedItem | null {
  if (elementItemId === null) return null;
  const root = asObj(response);
  if (!root) return null;
  for (const el of asArr(root[RES.elements])) {
    for (const it of asArr(field(el, RES.items))) {
      const itObj = asObj(it);
      if (itObj && toNumber(itObj[RES.id]) === elementItemId) {
        return {
          name:
            pickItemName(itObj[RES.itemNames], lookups.lang) ??
            lookups.itemName(itObj[RES.itemId]),
          unit: toNumber(itObj[RES.price]),
          quantity: toNumber(itObj[RES.quantity]),
        };
      }
    }
  }
  return null;
}

function resolveItemNameByElementItemId(
  response: unknown,
  elementItemId: number | null,
  lookups: AuditLookups,
): string | null {
  return resolveBillItem(response, elementItemId, lookups)?.name ?? null;
}

// Split връща { oldBill, newBill }; преместеният елемент може да остане в
// старата сметка (частично местене) или да е в новата. Търсим в двете.
function resolveSplitItem(
  response: unknown,
  elementItemId: number | null,
  lookups: AuditLookups,
): ResolvedItem | null {
  const root = asObj(response);
  if (!root) return null;
  for (const key of [RES.splitOldBill, RES.splitNewBill] as const) {
    const bill = asObj(root[key]);
    const item = bill
      ? resolveBillItem(bill, elementItemId, lookups)
      : null;
    if (item) return item;
  }
  return null;
}

// ── Продуктови редове от заявката (нова/добавяне) ───────────────────────────

interface NumLine {
  qtyNum: number;
  name: string;
  unitNum: number | null;
  totalNum: number;
  addons: AddonLine[];
  subs: string[];
}

function buildProductLines(
  body: unknown,
  lookups: AuditLookups,
  t: TFunction,
): { lines: Line[]; total: number } {
  const raw: NumLine[] = [];
  let total = 0;
  for (const r of asArr(field(body, REQ.elements))) {
    const el = asObj(r);
    if (!el) continue;
    const productQty = toNumber(el[REQ.quantity]) ?? 0;
    const lineTotal = toNumber(el[REQ.lineTotal]) ?? 0;
    // Продуктът (lineTotal) е база без добавки — те се сборуват отделно.
    total += lineTotal;

    // Добавките се записват на единица продукт (quantity:"1", total_sum = ед.
    // цена); реалната им тежест в сметката е × количеството на продукта.
    const addons: AddonLine[] = [];
    for (const a of asArr(el[REQ.addons])) {
      const ad = asObj(a);
      if (!ad) continue;
      const mult = productQty || 1;
      const perUnit = toNumber(ad[REQ.addonTotal]) ?? 0;
      const effTotal = perUnit * mult;
      const effQty = (toNumber(ad[REQ.quantity]) ?? 1) * mult;
      const unitPrice = toNumber(ad[REQ.price]);
      total += effTotal;
      addons.push({
        qty: qty(effQty),
        name: lookups.itemName(ad[REQ.addonItemId]),
        unit: unitPrice !== null ? eur(unitPrice) : undefined,
        total: eur(effTotal),
      });
    }

    const subs: string[] = [];
    // знаков процент: >0 отстъпка, <0 надбавка (pct показва „+X%")
    const percent = toNumber(el[REQ.percent]) ?? 0;
    if (percent !== 0)
      subs.push(`${t("auditLogDetail.human.discount")}: ${pct(percent)}`);
    for (const mraw of asArr(el[REQ.modifiers])) {
      const mn = lookups.modifierName(field(mraw, REQ.modifierId));
      if (mn && mn !== "—")
        subs.push(`${t("auditLogDetail.human.modifier")}: ${mn}`);
    }

    raw.push({
      qtyNum: productQty,
      name: lookups.itemName(el[REQ.productId]),
      unitNum: toNumber(el[REQ.price]),
      totalNum: lineTotal,
      addons,
      subs,
    });
  }

  // Сливаме еднаквите прости редове (един продукт, една цена, без добавки и
  // бележки), за да не изглеждат като дубликати (напр. 2× + 1× → 3×).
  const merged: NumLine[] = [];
  for (const ln of raw) {
    const same =
      ln.addons.length === 0 && ln.subs.length === 0
        ? merged.find(
            (m) =>
              m.addons.length === 0 &&
              m.subs.length === 0 &&
              m.name === ln.name &&
              m.unitNum === ln.unitNum,
          )
        : undefined;
    if (same) {
      same.qtyNum += ln.qtyNum;
      same.totalNum += ln.totalNum;
    } else {
      merged.push({ ...ln });
    }
  }

  const lines: Line[] = merged.map((m) => ({
    qty: qty(m.qtyNum),
    name: m.name,
    unit: m.unitNum !== null ? eur(m.unitNum) : undefined,
    total: eur(m.totalNum),
    tone: "add",
    addons: m.addons.length ? m.addons : undefined,
    subs: m.subs.length ? m.subs : undefined,
  }));
  return { lines, total };
}

// ── Главна функция ──────────────────────────────────────────────────────────

export function interpretAudit(
  detail: AuditLogDetail,
  lookups: AuditLookups,
  t: TFunction,
): HumanStory {
  const kind = classifyAction(detail.method, detail.path, detail.request_body);
  const body = detail.request_body;
  const resp = detail.response_body;
  const defectiveOf = (o: Record<string, unknown>) =>
    o[REQ.isDefective] ?? o[REQ.isDefectiveAlt];
  const yesNo = (v: unknown) =>
    v === true ? t("auditLogDetail.human.yes") : t("auditLogDetail.human.no");

  const base = (tone: Tone): HumanStory => ({
    kind,
    tone,
    facts: [],
    lines: [],
    interpreted: true,
  });

  switch (kind) {
    case "new":
    case "add": {
      const story = base("add");
      if (isTruncated(body)) {
        story.interpreted = false;
        return story;
      }
      const { lines, total } = buildProductLines(body, lookups, t);
      story.lines = lines;
      story.linesLabel = t("auditLogDetail.human.products");
      if (total > 0) {
        story.total = eur(total);
        story.totalLabel = t("auditLogDetail.human.addedAmount");
      }
      const comment = asString(field(body, REQ.comment));
      if (comment) story.note = comment;
      story.interpreted = lines.length > 0;
      return story;
    }

    case "applyDiscount": {
      const o = asObj(body) ?? {};
      const story = base("discount");
      story.facts.push({
        label: t("auditLogDetail.human.percent"),
        value: pct(o[REQ.percent]),
        tone: "discount",
      });
      const clientName = asString(o[REQ.clientName]);
      if (clientName)
        story.facts.push({ label: t("auditLogDetail.human.client"), value: clientName });
      const discountType = asString(o[REQ.discountType]);
      if (discountType)
        story.facts.push({
          label: t("auditLogDetail.human.discountType"),
          value: discountType,
        });
      return story;
    }

    case "removeDiscount": {
      const story = base("remove");
      story.note = t("auditLogDetail.human.removedDiscountNote");
      return story;
    }

    case "applyItemDiscount": {
      const o = asObj(body) ?? {};
      const story = base("discount");
      const name = resolveItemNameByElementItemId(
        resp,
        toNumber(o[REQ.billElementItemId]),
        lookups,
      );
      story.facts.push({
        label: t("auditLogDetail.human.product"),
        value: name ?? `#${qty(o[REQ.billElementItemId])}`,
      });
      story.facts.push({
        label: t("auditLogDetail.human.percent"),
        value: pct(o[REQ.percent]),
        tone: "discount",
      });
      return story;
    }

    case "removeItemDiscount": {
      const o = asObj(body) ?? {};
      const story = base("remove");
      const name = resolveItemNameByElementItemId(
        resp,
        toNumber(o[REQ.billElementItemId]),
        lookups,
      );
      story.facts.push({
        label: t("auditLogDetail.human.product"),
        value: name ?? `#${qty(o[REQ.billElementItemId])}`,
      });
      story.note = t("auditLogDetail.human.removedItemDiscountNote");
      return story;
    }

    case "applyItemDiscounts": {
      const story = base("discount");
      // Модалът праща само променените редове; 0% = махната отстъпка.
      story.lines = asArr(field(body, REQ.priceLines)).map((raw) => {
        const line = asObj(raw) ?? {};
        const name = resolveItemNameByElementItemId(
          resp,
          toNumber(line[REQ.billElementItemId]),
          lookups,
        );
        const percent = toNumber(line[REQ.percent]) ?? 0;
        return {
          qty: qty(line[REQ.quantity]),
          name: name ?? `#${qty(line[REQ.billElementItemId])}`,
          total: pct(line[REQ.percent]),
          tone: percent > 0 ? "discount" : "remove",
          subs:
            percent > 0
              ? undefined
              : [t("auditLogDetail.human.removedItemDiscountNote")],
        };
      });
      story.linesLabel = t("auditLogDetail.human.itemDiscounts");
      story.interpreted = story.lines.length > 0;
      return story;
    }

    case "setItemPrices": {
      const story = base("info");
      // Модалът праща само пипнатите редове; `additionId` различава добавка от
      // самия продукт, а името се резолвва през отговора (пълната сметка).
      story.lines = asArr(field(body, REQ.priceLines)).map((raw) => {
        const line = asObj(raw) ?? {};
        const name = resolveItemNameByElementItemId(
          resp,
          toNumber(line[REQ.billElementItemId]),
          lookups,
        );
        const isAddition = toNumber(line[REQ.additionId]) !== null;
        return {
          name: name ?? `#${qty(line[REQ.billElementItemId])}`,
          total: eur(line[REQ.price]),
          subs: isAddition
            ? [t("auditLogDetail.human.additionLine")]
            : undefined,
        };
      });
      story.linesLabel = t("auditLogDetail.human.newPrices");
      story.interpreted = story.lines.length > 0;
      return story;
    }

    case "cancelItem":
    case "cancelItems": {
      const o = asObj(body) ?? {};
      const story = base("remove");
      // `cancel-item` носи един ред в самото тяло, `cancel-items` (0.0.274) —
      // списък `items` с обща причина и „брак".
      const requestLines =
        kind === "cancelItems"
          ? asArr(o[REQ.cancelLines]).map((raw) => asObj(raw) ?? {})
          : [o];
      for (const line of requestLines) {
        const item = resolveBillItem(
          resp,
          toNumber(line[REQ.billElementItemId]),
          lookups,
        );
        // Сторнираното количество е от заявката; ако липсва — цялото количество.
        const cancelledQty =
          toNumber(line[REQ.quantity]) ?? item?.quantity ?? null;
        const unit = item?.unit ?? null;
        const amount =
          unit !== null && cancelledQty !== null ? unit * cancelledQty : null;
        story.lines.push({
          qty: cancelledQty != null ? qty(cancelledQty) : undefined,
          name:
            item?.name ??
            `${t("auditLogDetail.human.product")} #${qty(line[REQ.billElementItemId])}`,
          unit: unit !== null ? eur(unit) : undefined,
          // Минус за консистентност — сторното намалява сметката.
          total: amount !== null ? `−${eur(amount)}` : undefined,
          tone: "remove",
        });
      }
      story.interpreted = story.lines.length > 0;
      story.linesLabel = t("auditLogDetail.human.cancelled");
      const reason = asString(o[REQ.reason]);
      if (reason)
        story.facts.push({ label: t("auditLogDetail.human.reason"), value: reason });
      story.facts.push({
        label: t("auditLogDetail.human.defective"),
        value: yesNo(defectiveOf(o)),
      });
      return story;
    }

    case "cancelAll": {
      const o = asObj(body) ?? {};
      const story = base("remove");
      story.note = t("auditLogDetail.human.cancelledAllNote");
      const reason = asString(o[REQ.reason]);
      if (reason)
        story.facts.push({ label: t("auditLogDetail.human.reason"), value: reason });
      story.facts.push({
        label: t("auditLogDetail.human.defective"),
        value: yesNo(defectiveOf(o)),
      });
      return story;
    }

    case "close": {
      const o = asObj(body) ?? {};
      const story = base("money");
      story.facts.push({
        label: t("auditLogDetail.human.paymentMethod"),
        value: lookups.paymentMethodName(o[REQ.paymentMethodId]),
        tone: "money",
        inline: true,
      });
      const cash = toNumber(o[REQ.mixedCash]);
      const card = toNumber(o[REQ.mixedCard]);
      if (cash !== null && cash > 0)
        story.facts.push({
          label: t("auditLogDetail.human.cash"),
          value: eur(cash),
          inline: true,
        });
      if (card !== null && card > 0)
        story.facts.push({
          label: t("auditLogDetail.human.card"),
          value: eur(card),
          inline: true,
        });
      // Платената сума = салдото на сметката от отговора (заявката праща
      // total=0 при плащане "в брой"). Fallback към подадения total.
      const paid = billBalanceAfter(detail) ?? toNumber(o[REQ.total]);
      if (paid !== null) {
        story.total = eur(paid);
        story.totalLabel = t("auditLogDetail.human.paidTotal");
      }
      return story;
    }

    case "restore": {
      const story = base("info");
      story.note = t("auditLogDetail.human.restoredNote");
      return story;
    }

    case "split":
    case "splitAndPay": {
      const o = asObj(body) ?? {};
      const story = base("info");
      const elements = asArr(o[REQ.elements]);
      const targetTable = asString(o[REQ.splitTableName]);
      if (targetTable)
        story.facts.push({
          label: t("auditLogDetail.human.targetTable"),
          value: targetTable,
        });
      story.facts.push({
        label: t("auditLogDetail.human.movedItems"),
        value: String(elements.length),
      });

      // Преместени продукти: количество от заявката, име/ед. цена от отговора.
      // Минус — премахват се от текущата сметка (като сторно).
      const movedLines: Line[] = [];
      for (const e of elements) {
        const eo = asObj(e);
        if (!eo) continue;
        const beId = toNumber(eo[REQ.splitBillElementId]);
        const item = resolveSplitItem(resp, beId, lookups);
        const q = toNumber(eo[REQ.quantity]) ?? item?.quantity ?? null;
        const unit = item?.unit ?? null;
        const amount = unit !== null && q !== null ? unit * q : null;
        movedLines.push({
          qty: q != null ? qty(q) : undefined,
          name:
            item?.name ??
            `${t("auditLogDetail.human.product")} #${qty(beId ?? "")}`,
          unit: unit !== null ? eur(unit) : undefined,
          total: amount !== null ? `−${eur(amount)}` : undefined,
          tone: "remove",
        });
      }
      if (movedLines.length > 0) {
        story.lines = movedLines;
        story.linesLabel = t("auditLogDetail.human.movedProducts");
      }

      if (kind === "splitAndPay") {
        story.facts.push({
          label: t("auditLogDetail.human.paymentMethod"),
          value: lookups.paymentMethodName(o[REQ.paymentMethodId]),
          tone: "money",
          inline: true,
        });
        const total = toNumber(o[REQ.total]);
        if (total !== null) {
          story.total = eur(total);
          story.totalLabel = t("auditLogDetail.human.paidTotal");
        }
      }
      return story;
    }

    case "rename": {
      const o = asObj(body) ?? {};
      const story = base("info");
      const name = asString(o[REQ.name]);
      if (name)
        story.facts.push({
          label: t("auditLogDetail.human.newName"),
          value: name,
          inline: true,
        });
      return story;
    }

    case "changeWaiter": {
      const o = asObj(body) ?? {};
      const story = base("info");
      story.facts.push({
        label: t("auditLogDetail.human.waiter"),
        value: `#${qty(o[REQ.waiterId])}`,
      });
      return story;
    }

    case "cashIn":
    case "cashOut": {
      const o = asObj(body) ?? {};
      const story = base("money");
      let amount: number | null = null;
      for (const f of REQ.cashAmountFields) {
        amount = toNumber(o[f]);
        if (amount !== null) break;
      }
      if (amount !== null) {
        story.total = eur(amount);
        story.totalLabel =
          kind === "cashIn"
            ? t("auditLogDetail.human.cashIn")
            : t("auditLogDetail.human.cashOut");
      } else {
        story.interpreted = false;
      }
      return story;
    }

    default: {
      // Модулите извън „Маркиране" имат собствени интерпретатори; „generic" е
      // само за път, който никой не разпознава.
      const moduleStory =
        interpretInventory(kind, detail, lookups, t) ??
        interpretSystem(kind, detail, lookups, t) ??
        interpretReservation(kind, detail, t);
      if (moduleStory) return moduleStory;
      const fallback = emptyStory("generic", "neutral");
      fallback.interpreted = false;
      return fallback;
    }
  }
}
